import React, { RefObject, useEffect, useMemo, useRef, useState } from "react";
import useToggleNotebookReferencePanel from "./use-toggle-notebook-reference-panel";
import Tabs from "molecules/tabs/tabs";
import TabList from "molecules/tabs/tab-list";
import Tab from "atoms/tabs/tab";
import { t } from "utilities/localization-utils";
import TabPanels from "molecules/tabs/tab-panels";
import ReferencesFromPublicationList from "pages/notebooks/notebook-reference-panel/tabs/references-from-publication-list";
import ReferencesFromBookmarks from "pages/notebooks/notebook-reference-panel/tabs/references-from-bookmarks";
import TabPanel from "atoms/tabs/tab-panel";
import NotebookReferencePanelBookview from "./bookview/notebook-reference-panel-bookview";
import PublicationService from "utilities/services/publications/publication-service";
import { ToastManager } from "utilities/toast/toast-manager";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import { atom, useAtom } from "jotai";
import { CurrentTableOfContentsItem } from "pages/notebooks/notebook-reference-panel/bookview/use-virtual-table-of-contents";
import { NotebookReferencePanelRefAtom } from "utilities/atoms/notebook-reference-panel-ref-atom";
import { Resizable, ResizableProps } from "re-resizable";
import useResizeContainer from "utilities/hooks/use-resize-column";
import LocalStorageKey from "utilities/enumerations/local-storage-keys";
import useBreakpoint from "utilities/hooks/use-breakpoint";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useToggleNotebookSidebar from "pages/notebooks/use-toggle-notebook-sidebar";
import NotebookReferencePanelHeader from "pages/notebooks/notebook-reference-panel/notebook-reference-panel-header";
import CultureResources from "utilities/interfaces/culture-resources";
import { useOpenTocPanels } from "pages/notebooks/notebook-reference-panel/use-open-toc-panels";
import SectionRecord from "models/view-models/section-record";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import useNotebookBreakpoints from "utilities/hooks/domain/notebooks/use-notebook-breakpoints";

export const NOTEBOOK_REF_PANEL_CLASS_NAME = "c-notebook-reference-panel";
const DESKTOP_PANEL_MAX_WIDTH = "80vw";
const DESKTOP_PANEL_MIN_WIDTH = 300;
const DESKTOP_PANEL_STARTING_WIDTH = 40;

const MOBILE_PANEL_MAX_WIDTH = "100vw";
const MOBILE_PANEL_MIN_WIDTH = "100vw";
const MOBILE_PANEL_STARTING_HEIGHT = 100;
const PANEL_MIN_HEIGHT = "10vh";
const MOBILE_RESIZE_HOTSPOT = "20px";

export enum NotebookReferencePanelTabs {
    Publications,
    Bookmarks,
}

export const NotebookReferencePanelTabsNameMap = {
    [NotebookReferencePanelTabs.Publications]:
        "publication_plural" as keyof CultureResources,
    [NotebookReferencePanelTabs.Bookmarks]:
        "bookmark_plural" as keyof CultureResources,
};

export interface NotebookReferencePanelTabPanelProps {
    onPublicationClick?: (code: string, edition: string) => void;
}

const NotebookReferencePanel = () => {
    // May change active tab based on user settings, eventually
    const [activeTab, setActiveTab] = useState<NotebookReferencePanelTabs>(
        NotebookReferencePanelTabs.Publications
    );

    const { resetAllPanels } = useOpenTocPanels();

    const { showPanel } = useToggleNotebookReferencePanel();
    const shouldShow = showPanel ? "-open" : "-closed";
    const { publication, setCurrentPublication } = useCurrentPublication();
    const isDesktop = useBreakpoint(Breakpoints.Tablet);
    const { showSidebar, toggleDisplay: toggleLeftSidebar } =
        useToggleNotebookSidebar();
    const { isMobile } = useNotebookBreakpoints();

    // Stores ref as part of an atom so it can be referenced throughout the Notebook
    // Mainly to exclude toggle and inside clicks from useOutsideClick callbacks on other elements
    const notebookPanelRef = useRef<HTMLDivElement>(null);
    useAtom(
        useMemo(() => {
            const a = atom(
                (get) => get(NotebookReferencePanelRefAtom),
                (_, set, arg: RefObject<HTMLElement>) =>
                    set(NotebookReferencePanelRefAtom, arg)
            );
            a.onMount = (set) => {
                set(notebookPanelRef);
            };
            return a;
        }, [])
    );

    const showBookview = !!publication?.id;
    const [currentTocItem, setCurrentTocItem] = useAtom(
        CurrentTableOfContentsItem
    );

    const handlePublicationClick = async (
        code: string,
        edition: string,
        fromTab: NotebookReferencePanelTabs
    ) => {
        resetAllPanels();
        // Set loading state here to begin transition into bookview?
        try {
            const currentPublication = await getPublication(code, edition);
            setCurrentPublication(currentPublication);
            setActiveTab(fromTab);
        } catch (error) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("loading"),
                    resource: t("publication"),
                })
            );
        }
    };

    const handleBookmarkClick = async (
        section: SectionRecord,
        code: string,
        edition: string,
        parentNfpaLabel: string
    ) => {
        try {
            const currentPublication = await getPublication(code, edition);
            setCurrentPublication(currentPublication);
            setCurrentTocItem({
                id: section.id!,
                parentId: `${section.annexId || section.articleId || section.chapterId}`,
                nfpaLabel: `${parentNfpaLabel}`,
                publicationType: section.annexId
                    ? PublicationComponentType.Annex
                    : PublicationComponentType.Section,
                annexId: section.annexId,
            });
            setActiveTab(NotebookReferencePanelTabs.Bookmarks);
        } catch (error) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("loading"),
                    resource: t("bookmark"),
                })
            );
        }
    };

    const {
        defaultResizableDimension,
        handleResizeStop,
        isResizing,
        handleResizeStart,
    } = useResizeContainer({
        id: LocalStorageKey.NotebookLayoutReferencePanelWidthInVw,
        startingResizableDimension: isMobile
            ? MOBILE_PANEL_STARTING_HEIGHT
            : DESKTOP_PANEL_STARTING_WIDTH,
        isHorizontal: isDesktop,
    });

    // Closes leftside if reference panel is resized to more than 50% of screen
    useEffect(() => {
        if (defaultResizableDimension > 50 && showSidebar && showPanel) {
            toggleLeftSidebar();
        }
    }, [defaultResizableDimension, showPanel, showSidebar, toggleLeftSidebar]);

    const desktopResizableProps: ResizableProps = {
        enable: { left: true },
        handleStyles: {
            left: {
                width: "24px",
                left: "-12px",
                top: 0,
                bottom: 0,
                cursor: "ew-resize",
                zIndex: 10,
            },
        },
        maxWidth: DESKTOP_PANEL_MAX_WIDTH,
        minHeight: "100%",
        minWidth: DESKTOP_PANEL_MIN_WIDTH,
    };
    const mobileResizableProps: ResizableProps = {
        enable: { top: true },
        handleStyles: {
            top: {
                width: "100vw",
                height: MOBILE_RESIZE_HOTSPOT,
                bottom: 0,
                left: 0,
                right: 0,
                cursor: "ns-resize",
                zIndex: 10,
            },
        },
        maxWidth: MOBILE_PANEL_MAX_WIDTH,
        minHeight: PANEL_MIN_HEIGHT,
        minWidth: MOBILE_PANEL_MIN_WIDTH,
    };
    const resizableProps = isDesktop
        ? desktopResizableProps
        : mobileResizableProps;
    const Wrapper = useMemo(
        () =>
            ({ children }) =>
                showPanel ? (
                    <Resizable
                        className={`${NOTEBOOK_REF_PANEL_CLASS_NAME}__resizable`}
                        defaultSize={{
                            width: `${defaultResizableDimension}vw`,
                        }}
                        maxHeight={"100%"}
                        onResizeStart={handleResizeStart}
                        onResizeStop={handleResizeStop}
                        {...resizableProps}>
                        {children}
                    </Resizable>
                ) : (
                    <>{children}</>
                ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [showPanel, isDesktop]
    );

    // Passing in current width as CSS Custom Prop to allow for setting a width for CSS transitions
    const style = {
        "--currentNotebookPanelWidth": `${defaultResizableDimension ?? 40}vw`,
    } as React.CSSProperties;

    const isResizingDirectionModifier = isDesktop
        ? "-isResizingHorizontally"
        : "-isResizingVertically";
    const isResizingModifier = isResizing ? isResizingDirectionModifier : "";
    const isMobileModifier = isMobile ? "-isMobile" : "";
    const resizingModifier = `${NOTEBOOK_REF_PANEL_CLASS_NAME} ${shouldShow} ${isResizingModifier} ${isMobileModifier}`;

    return (
        <>
            {isMobile && (
                <div
                    className={`${NOTEBOOK_REF_PANEL_CLASS_NAME}__mobileOverlay ${shouldShow}`}
                />
            )}
            <div
                ref={notebookPanelRef}
                style={style}
                className={resizingModifier}>
                <Wrapper>
                    <header
                        className={`${NOTEBOOK_REF_PANEL_CLASS_NAME}__header`}>
                        <NotebookReferencePanelHeader
                            activeTab={activeTab}
                            showBookview={showBookview}
                            currentPublication={publication}
                            currentTocItem={currentTocItem}
                            setCurrentTocItem={setCurrentTocItem}
                            setCurrentPublication={setCurrentPublication}
                        />
                    </header>
                    <div className={`${NOTEBOOK_REF_PANEL_CLASS_NAME}__body`}>
                        {/* Tab View */}
                        {!showBookview && (
                            <div
                                className={`${NOTEBOOK_REF_PANEL_CLASS_NAME}__tabs`}>
                                <Tabs defaultIndex={activeTab}>
                                    <TabList
                                        cssClassName={`${NOTEBOOK_REF_PANEL_CLASS_NAME}__tabList`}>
                                        <Tab
                                            cssClassName={`${NOTEBOOK_REF_PANEL_CLASS_NAME}__tab`}>
                                            {t(
                                                NotebookReferencePanelTabsNameMap[
                                                    NotebookReferencePanelTabs
                                                        .Publications
                                                ]
                                            )}
                                        </Tab>
                                        <Tab
                                            cssClassName={`${NOTEBOOK_REF_PANEL_CLASS_NAME}__tab`}>
                                            {t(
                                                NotebookReferencePanelTabsNameMap[
                                                    NotebookReferencePanelTabs
                                                        .Bookmarks
                                                ]
                                            )}
                                        </Tab>
                                    </TabList>
                                    <TabPanels>
                                        <TabPanel>
                                            <ReferencesFromPublicationList
                                                onPublicationClick={(
                                                    code,
                                                    edition
                                                ) =>
                                                    handlePublicationClick(
                                                        code,
                                                        edition,
                                                        NotebookReferencePanelTabs.Publications
                                                    )
                                                }
                                            />
                                        </TabPanel>
                                        <TabPanel>
                                            <ReferencesFromBookmarks
                                                onSectionClick={
                                                    handleBookmarkClick
                                                }
                                            />
                                        </TabPanel>
                                    </TabPanels>
                                </Tabs>
                            </div>
                        )}
                        {/* Book View */}
                        {showBookview && (
                            <NotebookReferencePanelBookview
                                publication={publication}
                            />
                        )}
                    </div>
                </Wrapper>
            </div>
        </>
    );
};

const getPublication = async (code: string, edition: string) => {
    try {
        const result = await PublicationService.list({
            code,
            edition,
        });

        const currentPublication = {
            publication: result.resultObjects[0],
            loaded: true,
            errors: [],
        };
        if (!currentPublication) {
            throw new Error();
        }

        return currentPublication;
    } catch (error) {
        throw new Error();
    }
};

export default NotebookReferencePanel;
